import { ViewId } from "@msidentity/SISU/constants";
import { useNavigateDirection, useProgressIndicator } from "@msidentity/SISU/hooks";
import { MemberNameType } from "@msidentity/SISU/model/user";
import { type ICountryInfo } from "@msidentity/SISU/utilities/country-helper";
import { type SendOttResponse, sendOtt } from "../../../../utilities/api-helpers";
import UsernameRecoveryConfig from "../../username-recovery-config";
import { useUsernameRecoveryContext } from "../../username-recovery-context";
import { UsernameRecoveryActionType } from "../../username-recovery-reducer";
import { type UsernameRecoveryMemberNamePrefill } from "../../username-recovery-types";
import { getSendOttParams } from "../get-send-ott-params";
import { useSendOttFailureHandler } from "./use-send-ott-failure-handler";

export type SendOttParams = {
  viewId: ViewId;
  proofId: string;
  phoneCountry: ICountryInfo;
  memberNameType: UsernameRecoveryMemberNamePrefill;
};

/**
 * A reusable hook to handle the success of a SendOtt API call
 * @param params The parameters required for the API call. These are taken as params (not loaded from Context) as they might not update before we want to call.
 * @returns A callback to handle the successful response of a SendOtt API call
 */
export const useSendOttSuccessHandler = (params: SendOttParams) => {
  const { proofId, phoneCountry, memberNameType, viewId } = params;
  const navigate = useNavigateDirection();
  const { dispatchStateChange } = useUsernameRecoveryContext();

  return (response: Partial<SendOttResponse>) => {
    // Save the username display for re-use
    let usernameDisplay = proofId;
    if (memberNameType === MemberNameType.Phone) {
      const { formattedInternationalPhoneNumber } = response;
      if (formattedInternationalPhoneNumber) {
        usernameDisplay = formattedInternationalPhoneNumber;
      } else {
        usernameDisplay = phoneCountry.code.concat(proofId);
      }
    }

    dispatchStateChange({
      type: UsernameRecoveryActionType.SetUsernameDisplay,
      usernameDisplay,
    });

    // Replace state when navigating from Challenge to remove the view from history
    navigate(viewId, ViewId.UsernameRecoveryLegacyCode, false, {
      replace: viewId === ViewId.Challenge,
    });
  };
};

/**
 * A reusable hook to call SendOtt for UsernameRecovery
 * @param params The parameters required for the API call. These are taken as params (not loaded from Context) as they might not update before we want to call.
 * @returns A callback to call SendOtt
 */
export const useSendOtt = (params: SendOttParams) => {
  const { proofId, phoneCountry, memberNameType, viewId } = params;
  const {
    sendCodeInfo: { sendOttUrl, sendOttAction },
  } = UsernameRecoveryConfig.instance;

  const { showProgressIndicator, hideProgressIndicator } = useProgressIndicator();
  const successHandler = useSendOttSuccessHandler(params);
  const failureHandler = useSendOttFailureHandler(viewId);
  const sendOttParams = getSendOttParams(sendOttAction, memberNameType, phoneCountry, proofId);

  return async () => {
    showProgressIndicator();
    await sendOtt(sendOttUrl, sendOttParams).then(successHandler, failureHandler);
    hideProgressIndicator();
  };
};
